import React, { useRef, useState, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import CommonDropdown from "../common/common_dropDown";
import CommonStaticDropdown from "../common/common_static_dropdown";
import { submitVehicleAPI, updateVehicleAPI, getVehicleEditApi, saveAttachmentApi } from "./vehicle-api";
import "../common/Util.css";
import uploadIcon from "../../assets/svg/upload_file.svg";
import closeAttachment from "../../assets/svg/close-red.svg";
import "../common/skeleton-effect.css";
import CommonSnackbar from "../common/reusable-commponent";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";

export const VehicleCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (id != null) {
      getVehicleEdit(id);
    }
  }, [id]);

  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const [isLoader, setIsLoader] = useState(false);

  const triggerSnackbar = (message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  };

  // Form fields
  const [vin, setVin] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [vehicleType, setVehicleType] = useState(null);
  const [vehicleModel, setVehicleModel] = useState("");
  const [yearOfManufacture, setYearOfManufacture] = useState("");
  const [vehicleTrim, setVehicleTrim] = useState("");
  const [vehicleKM, setVehicleKM] = useState("");
  const [ownershipOption, setOwnershipOption] = useState(null);
  const [colorOption, setColorOption] = useState(null);
  const [bodyType, setBodyType] = useState(null);
  const [loadCapacity, setLoadCapacity] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [groupLocation, setGroupLocation] = useState(null);

  const [vehicleImages, setVehicleImages] = useState([]);
  const [vehicleUrlImages, setVehicleUrlImages] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);

  const [imagePreview, setImagePreview] = useState(null);
  const [statusValue, setStatusValue] = useState({
    label: "Active",
    value: true,
  });
  const [statusList, setStatusList] = useState([
    { label: "Active", value: true },
    { label: "In-Active", value: false },
  ]);
  const [measurement, setMeasurement] = useState("");
  const [measurementTypeList, setMeasurementTypeList] = useState([
    { label: "Inch", value: "Inch" },
    { label: "ft", value: "ft" },
    { label: "cm", value: "cm" },
  ]);

  const [errorMsg, setErrorMsg] = useState({
    vin: "",
    vehicle_name: "",
    license_plate: "",
    type_id: "",
    model: "",
    year_of_manufacture: "",
    vehicle_trim: "",
    vehicle_km: "",
    ownership: "",
    vehicle_color: "",
    body_type: "",
    load_capacity: "",
    dimension: "",
    length: "",
    width: "",
    height: "",
    group_location_id: "",
    status: "",
  });

  const getVehicleEdit = async (vehicleId) => {
    setIsLoader(true);
    try {
      const response = await getVehicleEditApi(vehicleId);
      if (response && response.data && response.data.success === true) {
        const vehicle = response.data.vehicle ?? {};
        setVin(sanitizeInput(String(vehicle.vin ?? "")));
        setVehicleName(sanitizeInput(String(vehicle.vehicle_name ?? "")));
        setLicensePlate(sanitizeInput(String(vehicle.license_plate ?? "")));
        setVehicleType({
          value: vehicle.type_id,
          label: sanitizeInput(String(vehicle.type_label ?? "")),
        });
        setVehicleModel(sanitizeInput(String(vehicle.model ?? "")));
        setYearOfManufacture(sanitizeInput(String(vehicle.year_of_manufacture ?? "")));
        setVehicleTrim(sanitizeInput(String(vehicle.vehicle_trim ?? "")));
        setVehicleKM(sanitizeInput(String(vehicle.vehicle_km ?? "")));
        setOwnershipOption({
          value: sanitizeInput(String(vehicle.ownership ?? "")),
          label: sanitizeInput(String(vehicle.ownership ?? "")),
        });
        setColorOption({
          value: sanitizeInput(String(vehicle.vehicle_color ?? "")),
          label: sanitizeInput(String(vehicle.vehicle_color ?? "")),
        });
        setBodyType({
          value: sanitizeInput(String(vehicle.body_type ?? "")),
          label: sanitizeInput(String(vehicle.body_type ?? "")),
        });
        setLoadCapacity(sanitizeInput(String(vehicle.load_capacity ?? "")));
        setMeasurement(sanitizeInput(String(vehicle.dimension ?? "")));
        setLength(sanitizeInput(String(vehicle.length ?? "")));
        setWidth(sanitizeInput(String(vehicle.width ?? "")));
        setHeight(sanitizeInput(String(vehicle.height ?? "")));
        setGroupLocation({
          value: vehicle.group_location_id,
          label: sanitizeInput(String(vehicle.group_location ?? "")),
        });
        setVehicleUrlImages(vehicle.Attachments ?? []);
      }
    } catch {
      // Error handled by service
    } finally {
      setIsLoader(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setVehicleImages([...vehicleImages, ...newImages]);
    if (newImages.length > 0) {
      setImagePreview(newImages[0].preview);
    }
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    const newImages = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setVehicleImages([...vehicleImages, ...newImages]);
    if (newImages.length > 0) {
      setImagePreview(newImages[0].preview);
    }
  };

  const removeImage = (index) => {
    const updated = vehicleImages.filter((_, i) => i !== index);
    setVehicleImages(updated);
    if (updated.length > 0) {
      setImagePreview(updated[0].preview);
    } else {
      setImagePreview(null);
    }
  };

  const removeUrlImage = (index) => {
    const updated = vehicleUrlImages.filter((_, i) => i !== index);
    const removed = vehicleUrlImages[index];
    setVehicleUrlImages(updated);

    if (removed && removed.id) {
      setRemovedAttachmentIds([...removedAttachmentIds, removed.id]);
    }

    if (removed && imagePreview === removed.file_path) {
      setImagePreview(updated.length > 0 ? updated[0].file_path : null);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!vin.trim()) errors.vin = "VIN is required";
    if (!vehicleName.trim()) errors.vehicle_name = "Vehicle Name is required";
    if (!licensePlate.trim()) errors.license_plate = "License Plate is required";
    if (!vehicleType?.value) errors.vehicle_type = "Vehicle Type is required";
    if (!vehicleModel.trim()) errors.model = "Vehicle Model is required";
    if (!yearOfManufacture?.value) errors.year_of_manufacture = "Year of Manufacture is required";
    if (!loadCapacity.trim()) errors.load_capacity = "Load Capacity is required";

    setErrorMsg(errors);
    return Object.keys(errors).length === 0;
  };

  const clearForm = () => {
    setVin("");
    setVehicleName("");
    setLicensePlate("");
    setVehicleType(null);
    setVehicleModel("");
    setYearOfManufacture("");
    setVehicleTrim("");
    setVehicleKM("");
    setOwnershipOption(null);
    setColorOption(null);
    setBodyType(null);
    setLoadCapacity("");
    setLength("");
    setWidth("");
    setHeight("");
    setGroupLocation(null);
    setVehicleImages([]);
    setVehicleUrlImages([]);
    setImagePreview(null);
    setStatusValue({ label: "Active", value: true });
    setMeasurement("");
    setErrorMsg({
      vin: "",
      vehicle_name: "",
      license_plate: "",
      type_id: "",
      model: "",
      year_of_manufacture: "",
      vehicle_trim: "",
      vehicle_km: "",
      ownership: "",
      vehicle_color: "",
      body_type: "",
      load_capacity: "",
      dimension: "",
      length: "",
      width: "",
      height: "",
      group_location_id: "",
      status: "",
    });
  };

  const handleSubmit = async () => {
    setIsLoader(true);
    const preSanitizeData = {
      vin,
      vehicle_name: vehicleName,
      license_plate: licensePlate,
      model: vehicleModel,
      type_id: vehicleType?.value ?? "",
      year_of_manufacture: yearOfManufacture ?? "",
      vehicle_trim: vehicleTrim,
      vehicle_km: vehicleKM,
      ownership: ownershipOption?.value ?? "",
      group_location_id: groupLocation?.value ?? "",
      vehicle_color: colorOption?.value ?? "",
      load_capacity: loadCapacity,
      body_type: bodyType?.value ?? "",
      dimension: measurement,
      length: length,
      width: width,
      height: height,
      status: statusValue?.value ?? "",
    };

    if (id && removedAttachmentIds.length > 0) {
      preSanitizeData.removed_attachment = removedAttachmentIds;
    }

    try {
      const response = id ? await updateVehicleAPI(id, preSanitizeData) : await submitVehicleAPI(preSanitizeData);

      if (response.status === 400) {
        new Promise((resolve, reject) => {
          const validationTemp = {
            vin: "",
            vehicle_name: "",
            license_plate: "",
            type_id: "",
            model: "",
            year_of_manufacture: "",
            vehicle_trim: "",
            vehicle_km: "",
            ownership: "",
            vehicle_color: "",
            body_type: "",
            load_capacity: "",
            dimension: "",
            length: "",
            width: "",
            height: "",
            group_location_id: "",
          };
          Object.entries(response.data.error).forEach(([key, value]) => {
            validationTemp[key] = value.length === 0 ? "" : value[0];
          });
          setErrorMsg(validationTemp);
        });
      } else if (response && response.data && response.data.success === true) {
        triggerSnackbar(response.data.message || "Vehicle saved successfully!", true);
        if (vehicleImages.length === 0) {
          navigate(-1)
        } else {
          saveAttachment(response.data.id);
        }
      } else {
        triggerSnackbar(response.data?.message || "Error saving vehicle", false);
      }
    } catch (err) {
      const errorMessages = err.response?.data?.errors || {};
      setErrorMsg((prev) => ({ ...prev, ...errorMessages }));
    } finally {
      setIsLoader(false);
    }
  };

  const saveAttachment = async (parentId) => {
    const fileToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });

    let imagesBase64 = [];
    try {
      imagesBase64 = await Promise.all(vehicleImages.map((img) => fileToBase64(img.file)));
      const data = {
        attachments: imagesBase64,
      };
      const response = await saveAttachmentApi(parentId, data);
      if (response && response.data && response.data.success === true) {
        navigate(-1);
      }
    } catch {
      triggerSnackbar("Error uploading images", false);
    } finally {
    }
  };

  return (
    <>
      <div className="mt-2">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-light rounded-circle" onClick={() => navigate(-1)} style={{ width: 40, height: 40 }}>
              <MoveLeft size={20} />
            </button>
            <h5 className="fw-700 mb-0">{id ? "Edit Vehicle" : "New Vehicle"}</h5>
          </div>
          <div className="d-flex gap-3">
            <Button
              className="cursor-pointer bg-white rounded-pill px-4 py-2  btn-border-grey fs-15 fw-500"
              style={{ border: "1px solid #D4DDE7", color: "#212129" }}
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </Button>
            <Button
              className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
              onClick={() => {
                handleSubmit();
              }}
              style={{ minWidth: "100px" }}
            >
              {isLoader ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
            </Button>
          </div>
        </div>

        <div className="">
          {isLoader && id ? (
            // Skeleton Loading UI for Edit Mode
            <div className="row">
              {/* Left Column Skeleton */}
              <div className="col-lg-7">
                <div className="bg-white rounded-4 p-3">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "150px" }}></div>
                  <div className="row">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div key={i} className="col-md-6 mt-3">
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "100px", height: "12px" }}></div>
                        <div className="skeleton skeleton-text" style={{ width: "100%", height: "38px", borderRadius: "8px" }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column Skeleton */}
              <div className="col-lg-5">
                <div className="bg-white rounded-4 p-2">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "140px" }}></div>
                  <div className="skeleton skeleton-card mb-3" style={{ height: "200px", borderRadius: "12px" }}></div>
                  <div className="d-flex flex-wrap gap-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="skeleton skeleton-image"></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Row Skeleton */}
              <div className="col-lg-6 mt-2">
                <div className="bg-white rounded-4 p-3">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "150px" }}></div>
                  <div className="row">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="col-md-4 mt-2">
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "80px", height: "12px" }}></div>
                        <div className="skeleton skeleton-text" style={{ width: "100%", height: "38px", borderRadius: "8px" }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 mt-2">
                <div className="bg-white rounded-4 p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="skeleton skeleton-text-lg" style={{ width: "180px" }}></div>
                    <div className="skeleton skeleton-text-sm" style={{ width: "72px", height: "24px" }}></div>
                  </div>
                  <div className="row">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="col-md-4 mt-2">
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px", height: "12px" }}></div>
                        <div className="skeleton skeleton-text" style={{ width: "100%", height: "38px", borderRadius: "8px" }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="row">
                {/* Left Column: Vehicle Details */}
                <div className="col-lg-7 ">
                  <div className="bg-white rounded-4 p-3">
                    <h6 className="fw-700 text-start">Vehicle details</h6>

                    <div className="row">
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">
                            VIN<span style={{ color: "red" }}>*</span>
                          </label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={vin} maxLength={50} onChange={(e) => setVin(sanitizeInput(e.target.value))} />
                          {errorMsg.vin && <small className="text-danger">{errorMsg.vin}</small>}
                        </div>
                      </div>
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">
                            Vehicle Name<span style={{ color: "red" }}>*</span>
                          </label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={vehicleName} maxLength={100} onChange={(e) => setVehicleName(sanitizeInput(e.target.value))} />
                          {errorMsg.vehicle_name && <small className="text-danger">{errorMsg.vehicle_name}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6  mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">
                            License Plate<span style={{ color: "red" }}>*</span>
                          </label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={licensePlate} maxLength={20} onChange={(e) => setLicensePlate(sanitizeInput(e.target.value))} />
                          {errorMsg.license_plate && <small className="text-danger">{errorMsg.license_plate}</small>}
                        </div>
                      </div>
                      <div className="col-md-6  mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Vehicle Model</label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={vehicleModel} maxLength={50} onChange={(e) => setVehicleModel(sanitizeInput(e.target.value))} />
                          {errorMsg.model && <small className="text-danger">{errorMsg.model}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row ">
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Type</label>
                          <CommonDropdown apiUrl={`${apiUrl}get_vehicle_types`} placeholder="Select" value={vehicleType} onSelect={setVehicleType} handleClear={() => setVehicleType(null)} />

                          {errorMsg.type_id && <small className="text-danger">{errorMsg.type_id}</small>}
                        </div>
                      </div>
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Year of Manufacture</label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={yearOfManufacture} maxLength={4} onChange={(e) => setYearOfManufacture(sanitizeNumericInput(e.target.value))} />

                          {errorMsg.year_of_manufacture && <small className="text-danger">{errorMsg.year_of_manufacture}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row ">
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Vehicle Trim</label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={vehicleTrim} maxLength={50} onChange={(e) => setVehicleTrim(sanitizeInput(e.target.value))} />
                          {errorMsg.vehicle_trim && <small className="text-danger">{errorMsg.vehicle_trim}</small>}
                        </div>
                      </div>
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Vehicle KM</label>
                          <input type="text" className="form-control" placeholder="Enter here..." value={vehicleKM} maxLength={10} onChange={(e) => setVehicleKM(sanitizeNumericInput(e.target.value))} />
                          {errorMsg.vehicle_km && <small className="text-danger">{errorMsg.vehicle_km}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">
                            Ownership<span style={{ color: "red" }}>*</span>
                          </label>

                          <CommonDropdown apiUrl={`${apiUrl}vehicle/ownership`} placeholder="Select" value={ownershipOption} onSelect={setOwnershipOption} handleClear={() => setOwnershipOption(null)} />
                          {errorMsg.ownership && <small className="text-danger">{errorMsg.ownership}</small>}
                        </div>
                      </div>
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Group Location</label>
                          <CommonDropdown apiUrl={`${apiUrl}get_locations`} placeholder="Select" value={groupLocation} onSelect={setGroupLocation} handleClear={() => setGroupLocation(null)} />
                          {errorMsg.group_location_id && <small className="text-danger">{errorMsg.group_location_id}</small>}
                        </div>
                      </div>
                      <div className="col-md-6 mt-3">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Status</label>
                          <CommonStaticDropdown items={statusList} value={statusValue} onSelect={setStatusValue} placeholder="Select" />
                          {errorMsg.status && <small className="text-danger">{errorMsg.status}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Vehicle Image */}
                <div className="col-lg-5">
                  <div className="bg-white rounded-4 p-2">
                    <h6 className="fw-700 mb-3 text-start">Vehicle Image</h6>

                    {/* Main Preview */}
                    <div
                      className="rounded-3 p-4 mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        height: 200,
                        cursor: "pointer",
                        border: "1px solid #EBEBEB",
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleDragDrop}
                    >
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div className="text-center">
                          <img src={uploadIcon} alt="upload" className="mb-3" />

                          <p className="fs-13 fw-400 text-21272C mb-2">
                            Drag and drop or
                            <strong className="ms-2 fs-12 fw-400 text-3469E7">Select a file</strong>
                          </p>
                          <p className="text-gray fs-11 fw-400">You can only upload images here</p>
                        </div>
                      )}
                    </div>

                    {/* Thumbnail Gallery */}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {vehicleImages.map((img, idx) => (
                        <div key={`local-${idx}`} className="position-relative" style={{ width: 80, height: 80 }}>
                          <img
                            src={img.preview}
                            alt={`thumb-local-${idx}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: 8,
                              cursor: "pointer",
                              background: "#F5F5F5",
                              border: "none",
                              padding: "4px",
                            }}
                            onClick={() => setImagePreview(img.preview)}
                          />
                          <button
                            className="btn btn-sm position-absolute"
                            style={{
                              top: -8,
                              right: -8,
                              padding: "2px 6px",
                              fontSize: "10px",
                              borderRadius: "100px",
                              border: "1px solid #E5E5E5",
                              width: "21px",
                              height: "21px",
                              background: "#FFFFFF",
                            }}
                            onClick={() => removeImage(idx)}
                          >
                            <img src={closeAttachment} alt="close" />
                          </button>
                        </div>
                      ))}

                      {vehicleUrlImages.map((img, idx) => (
                        <div key={`url-${idx}`} className="position-relative" style={{ width: 80, height: 80 }}>
                          <img
                            src={img.file_path}
                            alt={`thumb-url-${idx}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: 8,
                              cursor: "pointer",
                              background: "#F5F5F5",
                              border: "none",
                              padding: "4px",
                            }}
                            onClick={() => setImagePreview(img.file_path)}
                          />
                          <button
                            className="btn btn-sm position-absolute"
                            style={{
                              top: -8,
                              right: -8,
                              padding: "2px 6px",
                              fontSize: "10px",
                              borderRadius: "100px",
                              border: "1px solid #E5E5E5",
                              width: "21px",
                              height: "21px",
                              background: "#FFFFFF",
                            }}
                            onClick={() => removeUrlImage(idx)}
                          >
                            <img src={closeAttachment} alt="close" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6 mt-2">
                  <div className="bg-white rounded-4 p-3">
                    <h6 className="fw-700 text-start">Additional Details</h6>

                    <div className="row">
                      <div className="col-md-4 mt-2">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Color</label>
                          <CommonDropdown apiUrl={`${apiUrl}vehicle/colors`} placeholder="Select" value={colorOption} onSelect={setColorOption} />
                          {errorMsg.vehicle_color && <small className="text-danger">{errorMsg.vehicle_color}</small>}
                        </div>
                      </div>
                      <div className="col-md-4  mt-2">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Load Capacity</label>
                          <input type="text" className="form-control" placeholder="Value" value={loadCapacity} maxLength={10} onChange={(e) => setLoadCapacity(sanitizeNumericInput(e.target.value))} />
                          {errorMsg.load_capacity && <small className="text-danger">{errorMsg.load_capacity}</small>}
                        </div>
                      </div>

                      <div className="col-md-4 mt-2">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Body Type</label>
                          <CommonDropdown apiUrl={`${apiUrl}vehicle/bodytype`} placeholder="Select" value={bodyType} onSelect={setBodyType} />
                          {errorMsg.body_type && <small className="text-danger">{errorMsg.body_type}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 mt-2">
                  <div className="bg-white rounded-4 p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="fw-700 text-start">Vehicle Dimension</h6>
                      <div style={{ width: "72px", height: "24px" }}>
                        <select className="select-placeholder" defaultValue="" value={measurement} onChange={(e) => setMeasurement(e.target.value)}>
                          <option value="" disabled>
                            Choose
                          </option>
                          {measurementTypeList.map((measure) => (
                            <option key={measure.value} value={measure.value}>
                              {measure.label}
                            </option>
                          ))}
                        </select>
                        {errorMsg.dimension && <small className="text-danger">{errorMsg.dimension}</small>}
                      </div>
                    </div>

                    <div className="row ">
                      <div className="col-md-4 mt-2">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Length</label>
                          <input type="text" className="form-control" placeholder="Value" value={length} maxLength={10} onChange={(e) => setLength(sanitizeNumericInput(e.target.value))} />
                          {errorMsg.length && <small className="text-danger">{errorMsg.length}</small>}
                        </div>
                      </div>
                      <div className="col-md-4 mt-2">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Width</label>
                          <input type="text" className="form-control" placeholder="Value" value={width} maxLength={10} onChange={(e) => setWidth(sanitizeNumericInput(e.target.value))} />
                          {errorMsg.width && <small className="text-danger">{errorMsg.width}</small>}
                        </div>
                      </div>
                      <div className="col-md-4 mt-2">
                        <div className="d-flex flex-column align-items-start">
                          <label className="form-label fs-13 fw-600 text-start vehicle-label">Height</label>
                          <input type="text" className="form-control" placeholder="Value" value={height} maxLength={10} onChange={(e) => setHeight(sanitizeNumericInput(e.target.value))} />
                          {errorMsg.height && <small className="text-danger">{errorMsg.height}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <CommonSnackbar show={snackbar.show} message={snackbar.message} isSuccess={snackbar.status} onClose={handleSnackbarClose} />
    </>
  );
};
